import type { NearbySpotItem } from '@/components/Functional/hooks-core/useApplicationTriggerFlowStateMachine'
import { Button } from '@/components/UI/Button'
import { Dialog } from '@/components/UI/Dialog'
import { IconChevronRight, IconQrcode, IconRubberStamp } from '@tabler/icons-react'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

// 表示項目の型は生成元（useApplicationTriggerFlowStateMachine）を単一の定義とする
export type { NearbySpotItem }

type Props = {
  opened: boolean
  onClose: () => void
  nearbySpots: NearbySpotItem[]
  /** キャンペーン全体のスタンプが全て QR_CODE か（暫定: false） */
  isAllQrCampaign: boolean
  onSelect: (spotId: number) => void
  gpsAvailable: boolean
  /** ダイアログタイトル。未指定時は「獲得可能なスポット・スタンプ」 */
  title?: string
}

function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)}m`
  return `${km.toFixed(1)}km`
}

export const DialogNearbySpots = memo(
  ({
    opened,
    onClose,
    nearbySpots,
    isAllQrCampaign,
    onSelect,
    gpsAvailable,
    title = '獲得可能なスポット・スタンプ',
  }: Props) => {
    const StampTypeIcon = isAllQrCampaign ? IconQrcode : IconRubberStamp
    const { t } = useTranslation()

    return (
      <Dialog selectorState={opened} closeFunc={onClose} title={title}>
        <div className="flex flex-col items-center gap-4">
          {!gpsAvailable && (
            <p className="w-full text-center text-xs text-gray-400">
              {t('bottom_navigation.nearby_spots_gps_fallback', '位置情報が取得できなかったためID順に表示しています')}
            </p>
          )}
          <div className="w-full">
            {nearbySpots.length === 0 ? (
              <p className="py-4 text-center text-[13px] text-gray-400">
                {t('bottom_navigation.nearby_spots_empty', '近くに獲得可能なスポットがありません')}
              </p>
            ) : (
              nearbySpots.map((spot) => (
                <button
                  key={spot.spotId}
                  type="button"
                  onClick={() => {
                    onSelect(spot.spotId)
                    onClose()
                  }}
                  // 行間は margin ではなく padding で確保する。margin だと背景色の外側になり、
                  // 灰色行と区切り線の間に白い隙間ができてしまうため（#3365）
                  className={`w-full flex items-center gap-3 px-0 py-2 h-20 border-b border-[#616161] last:border-b-0 text-left${
                    spot.grayedOut ? ' bg-gray-200' : ' bg-transparent'
                  }`}
                >
                  {spot.imageUrl ? (
                    <img
                      src={spot.imageUrl}
                      alt={spot.spotName}
                      className="w-16 h-16 rounded-2xl object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 shrink-0 flex items-center justify-center">
                      <StampTypeIcon size={28} className="text-main" aria-hidden="true" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-[16px] font-bold text-black leading-6 truncate">{spot.spotName}</p>
                    {spot.distanceKm != null && (
                      <p className="text-[12px] font-bold text-gray-500 leading-4">{formatDistance(spot.distanceKm)}</p>
                    )}
                    {spot.address && (
                      <p className="text-[12px] font-bold text-black leading-4 truncate">{spot.address}</p>
                    )}
                  </div>
                  <IconChevronRight size={24} className="shrink-0 text-[#cbcbce]" aria-hidden="true" />
                </button>
              ))
            )}
          </div>
          <div className="flex justify-center w-full">
            <Button
              label="OK"
              size="md"
              onClick={onClose}
              fullWidth
              className="[--button-bg:#eeeeee] [--button-color:#757575] [--button-hover:#e0e0e0]"
            />
          </div>
        </div>
      </Dialog>
    )
  },
)
